import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { ChevronDown, Clock, Download, ExternalLink, Loader2, Sparkles, ThumbsDown, ThumbsUp, Trash2, Wand2 } from 'lucide-react'
import { api, apiErrorMessage } from '../lib/api'
import type { RankResponse, RankedRowOut, SearchDetail, SearchSummary, StatusOut } from '../lib/api'
import { OutreachDrawer } from '../components/OutreachDrawer'
import { FacetsPanel } from '../components/FacetsPanel'
import { cn } from '../lib/cn'
import { downloadCsv, rowsToCsv } from '../lib/csv'

type Mode = 'fast' | 'best'

interface FeedbackEntry {
  profile_id: number
  vote: number
}

export function Search() {
  const qc = useQueryClient()
  const nav = useNavigate()

  const statusQ = useQuery<StatusOut>({
    queryKey: ['status'],
    queryFn: async () => (await api.get<StatusOut>('/status')).data,
  })

  // First-time users without enrichment go through onboarding.
  useEffect(() => {
    if (statusQ.data && statusQ.data.embedded === 0) {
      nav('/onboarding', { replace: true })
    }
  }, [statusQ.data, nav])

  const [query, setQuery] = useState('')
  const [topN, setTopN] = useState(10)
  const [pool, setPool] = useState(150)
  const [smart, setSmart] = useState(true)
  const [mode, setMode] = useState<Mode>('fast')

  const [result, setResult] = useState<RankResponse | null>(null)
  const [outreachTarget, setOutreachTarget] = useState<RankedRowOut | null>(null)
  const [historyOpen, setHistoryOpen] = useState(false)

  const rankMut = useMutation({
    mutationFn: async () => {
      const res = await api.post<RankResponse>('/rank', {
        query,
        top_n: topN,
        candidate_pool: pool,
        smart_query: smart,
        mode,
      })
      return res.data
    },
    onSuccess: (data) => {
      setResult(data)
      qc.invalidateQueries({ queryKey: ['searches'] })
    },
  })

  // Saved searches — auto-saved on every successful rank.
  const searchesQ = useQuery<SearchSummary[]>({
    queryKey: ['searches'],
    queryFn: async () => (await api.get<SearchSummary[]>('/searches')).data,
  })

  const loadSearch = useMutation({
    mutationFn: async (id: number) => (await api.get<SearchDetail>(`/searches/${id}`)).data,
    onSuccess: (data) => {
      setQuery(data.query_text)
      setMode(data.mode === 'best' ? 'best' : 'fast')
      setSmart(data.smart_query)
      setResult({ rows: data.rows, facets: data.facets, search_id: data.id })
    },
  })

  const deleteSearch = useMutation({
    mutationFn: async (id: number) => api.delete(`/searches/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['searches'] }),
  })

  // Existing feedback for the current query, keyed for the table.
  const feedbackQ = useQuery<FeedbackEntry[]>({
    queryKey: ['feedback', result?.facets.raw_query ?? query],
    queryFn: async () => {
      const q = result?.facets.raw_query ?? query
      if (!q) return []
      const res = await api.get<{ entries: FeedbackEntry[] }>('/feedback', {
        params: { query: q },
      })
      return res.data.entries
    },
    enabled: !!result,
  })

  const feedbackMap = useMemo(() => {
    const m = new Map<number, number>()
    for (const e of feedbackQ.data ?? []) m.set(e.profile_id, e.vote)
    return m
  }, [feedbackQ.data])

  const voteMut = useMutation({
    mutationFn: async ({ profile_id, vote }: { profile_id: number; vote: number }) => {
      const q = result?.facets.raw_query ?? query
      await api.post('/feedback', {
        query: q,
        votes: [{ profile_id, vote }],
      })
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['feedback'] })
    },
  })

  function onVote(row: RankedRowOut, want: 1 | -1) {
    const existing = feedbackMap.get(row.profile_id) ?? 0
    const next = existing === want ? 0 : want // toggle off
    voteMut.mutate({ profile_id: row.profile_id, vote: next })
  }

  function onDownloadCsv() {
    if (!result) return
    const headers = ['rank', 'name', 'title', 'company', 'location', 'score', 'reason', 'linkedin_url']
    const data = result.rows.map((r, i) => [
      i + 1,
      r.full_name,
      r.current_title || '',
      r.current_company || '',
      r.location || '',
      r.score.toFixed(4),
      r.reason || '',
      r.linkedin_url,
    ])
    const csv = rowsToCsv(headers, data)
    const safeQ = (result.facets.raw_query ?? query).replace(/[^a-z0-9]+/gi, '-').slice(0, 60).toLowerCase()
    const ts = new Date().toISOString().slice(0, 10)
    downloadCsv(`network-results-${safeQ || 'query'}-${ts}.csv`, csv)
  }

  return (
    <div className="px-8 py-6 max-w-6xl mx-auto">
      <header className="flex items-end justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-100">
            Who in your network should you reach out to?
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Hybrid retrieval over your 1st-degree LinkedIn connections, reranked, with reasons.
          </p>
        </div>
        {statusQ.data && (
          <div className="text-right text-xs text-gray-500">
            <div>
              {statusQ.data.enriched} of {statusQ.data.total_connections} enriched
            </div>
            <div>{statusQ.data.embedded} embedded · {statusQ.data.company_enriched} companies</div>
          </div>
        )}
      </header>

      <div className="card p-5 space-y-4">
        <textarea
          className="input min-h-[80px] resize-y leading-relaxed"
          placeholder="e.g. Early-stage SaaS founders in Bangalore who've sold to enterprise HR buyers"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />

        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 items-end">
          <div className="space-y-1">
            <label className="label">Top N</label>
            <input
              type="number"
              min={5}
              max={50}
              step={5}
              value={topN}
              onChange={(e) => setTopN(parseInt(e.target.value) || 10)}
              className="input"
            />
          </div>
          <div className="space-y-1">
            <label className="label">Candidate pool</label>
            <input
              type="number"
              min={50}
              max={300}
              step={50}
              value={pool}
              onChange={(e) => setPool(parseInt(e.target.value) || 150)}
              className="input"
            />
          </div>
          <div className="space-y-1">
            <label className="label">Smart query</label>
            <label className="flex items-center gap-2 h-9 px-1">
              <input
                type="checkbox"
                checked={smart}
                onChange={(e) => setSmart(e.target.checked)}
                className="h-4 w-4 accent-accent"
              />
              <span className="text-sm text-gray-300">Decompose facets</span>
            </label>
          </div>
          <div className="space-y-1">
            <label className="label">Mode</label>
            <select
              className="input"
              value={mode}
              onChange={(e) => setMode(e.target.value as Mode)}
            >
              <option value="fast">Fast · Cohere + Haiku</option>
              <option value="best">Best · Sonnet comparative</option>
            </select>
          </div>
          <button
            className="btn-primary h-9"
            disabled={!query.trim() || rankMut.isPending}
            onClick={() => rankMut.mutate()}
          >
            {rankMut.isPending ? (
              <>
                <Loader2 size={14} className="animate-spin" /> Ranking…
              </>
            ) : (
              <>
                <Sparkles size={14} /> Rank network
              </>
            )}
          </button>
        </div>
      </div>

      {rankMut.isError && (
        <div className="mt-4 text-sm text-red-400 bg-red-950/40 border border-red-900/40 rounded-md px-3 py-2">
          {apiErrorMessage(rankMut.error)}
        </div>
      )}

      {/* Saved searches — load past results without re-running. */}
      <div className="mt-6">
        <SearchHistory
          searches={searchesQ.data ?? []}
          loading={searchesQ.isLoading}
          open={historyOpen}
          onToggle={() => setHistoryOpen((v) => !v)}
          activeId={result?.search_id ?? null}
          onLoad={(id) => loadSearch.mutate(id)}
          onDelete={(id) => {
            if (confirm('Delete this saved search?')) deleteSearch.mutate(id)
          }}
          isLoadingId={loadSearch.isPending ? loadSearch.variables ?? null : null}
        />
      </div>

      {result && (
        <div className="mt-6 space-y-5">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-400">
              {result.rows.length} match{result.rows.length === 1 ? '' : 'es'}
            </div>
            <button
              className="btn-secondary h-8 text-xs"
              onClick={onDownloadCsv}
              disabled={result.rows.length === 0}
            >
              <Download size={14} /> Download CSV
            </button>
          </div>
          <ResultsTable
            rows={result.rows}
            feedbackMap={feedbackMap}
            onVote={onVote}
            onDraft={(row) => setOutreachTarget(row)}
          />
          {/* "How the query was understood" — collapsed by default, AFTER results. */}
          <FacetsPanel facets={result.facets} defaultOpen={false} />
        </div>
      )}

      <OutreachDrawer
        target={outreachTarget}
        useCase={result?.facets.raw_query ?? query}
        facets={result?.facets ?? null}
        onClose={() => setOutreachTarget(null)}
      />
    </div>
  )
}

function ResultsTable({
  rows,
  feedbackMap,
  onVote,
  onDraft,
}: {
  rows: RankedRowOut[]
  feedbackMap: Map<number, number>
  onVote: (row: RankedRowOut, want: 1 | -1) => void
  onDraft: (row: RankedRowOut) => void
}) {
  return (
    <div className="card overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-muted/50 text-gray-400 text-left">
          <tr>
            <th className="px-3 py-2 w-10">#</th>
            <th className="px-3 py-2">Person</th>
            <th className="px-3 py-2">Why a fit</th>
            <th className="px-3 py-2 w-20 text-right">Score</th>
            <th className="px-3 py-2 w-44 text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => {
            const vote = feedbackMap.get(row.profile_id) ?? 0
            return (
              <tr key={row.profile_id} className="border-t border-border/50 hover:bg-muted/20">
                <td className="px-3 py-3 text-gray-500 font-mono align-top">{i + 1}</td>
                <td className="px-3 py-3 align-top">
                  <div className="font-medium text-gray-100">{row.full_name}</div>
                  <div className="text-xs text-gray-400">
                    {row.current_title || '—'}
                    {row.current_company ? ` · ${row.current_company}` : ''}
                  </div>
                  {row.location && (
                    <div className="text-xs text-gray-500">{row.location}</div>
                  )}
                </td>
                <td className="px-3 py-3 text-gray-300 align-top">{row.reason || '—'}</td>
                <td className="px-3 py-3 text-right text-gray-400 font-mono align-top">
                  {row.score.toFixed(3)}
                </td>
                <td className="px-3 py-3 align-top">
                  <div className="flex items-center justify-end gap-1">
                    <button
                      title="Strong match"
                      className={cn(
                        'btn-ghost h-7 w-7 p-0',
                        vote === 1 && 'bg-green-500/15 text-green-400',
                      )}
                      onClick={() => onVote(row, 1)}
                    >
                      <ThumbsUp size={14} />
                    </button>
                    <button
                      title="Bad match"
                      className={cn(
                        'btn-ghost h-7 w-7 p-0',
                        vote === -1 && 'bg-red-500/15 text-red-400',
                      )}
                      onClick={() => onVote(row, -1)}
                    >
                      <ThumbsDown size={14} />
                    </button>
                    <button
                      title="Draft outreach"
                      className="btn-ghost h-7 w-7 p-0"
                      onClick={() => onDraft(row)}
                    >
                      <Wand2 size={14} />
                    </button>
                    <a
                      href={row.linkedin_url}
                      target="_blank"
                      rel="noreferrer"
                      title="Open LinkedIn"
                      className="btn-ghost h-7 w-7 p-0"
                    >
                      <ExternalLink size={14} />
                    </a>
                  </div>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

function relTime(iso: string): string {
  const t = Date.parse(iso + (iso.endsWith('Z') || iso.includes('+') ? '' : 'Z'))
  if (isNaN(t)) return iso
  const diff = (Date.now() - t) / 1000
  if (diff < 60) return 'just now'
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`
  return `${Math.floor(diff / 86400)}d ago`
}

function SearchHistory({
  searches,
  loading,
  open,
  onToggle,
  activeId,
  onLoad,
  onDelete,
  isLoadingId,
}: {
  searches: SearchSummary[]
  loading: boolean
  open: boolean
  onToggle: () => void
  activeId: number | null
  onLoad: (id: number) => void
  onDelete: (id: number) => void
  isLoadingId: number | null
}) {
  const count = searches.length
  return (
    <div className="card">
      <button
        className="w-full flex items-center justify-between px-5 py-3 text-sm font-medium text-gray-200"
        onClick={onToggle}
      >
        <span className="flex items-center gap-2">
          <Clock size={14} className="text-gray-500" />
          Recent searches
          <span className="text-xs text-gray-500 font-normal">
            {loading ? '…' : count === 0 ? '(none yet)' : `(${count})`}
          </span>
        </span>
        <ChevronDown size={16} className={cn('transition-transform', open && 'rotate-180')} />
      </button>
      {open && count > 0 && (
        <ul className="border-t border-border/50 max-h-72 overflow-auto">
          {searches.map((s) => (
            <li
              key={s.id}
              className={cn(
                'flex items-center gap-3 px-5 py-2 text-sm border-b border-border/30 last:border-b-0 hover:bg-muted/20',
                activeId === s.id && 'bg-muted/40',
              )}
            >
              <button
                className="flex-1 min-w-0 text-left"
                onClick={() => onLoad(s.id)}
                disabled={isLoadingId === s.id}
                title="Load this search's results"
              >
                <div className="flex items-center gap-2">
                  <div className="text-gray-200 truncate flex-1">{s.query_text || '(empty query)'}</div>
                  {isLoadingId === s.id && <Loader2 size={12} className="animate-spin text-gray-400" />}
                </div>
                <div className="text-xs text-gray-500">
                  {s.row_count} result{s.row_count === 1 ? '' : 's'} · {s.mode} · {relTime(s.created_at)}
                </div>
              </button>
              <button
                className="btn-ghost h-7 w-7 p-0 text-gray-500 hover:text-red-400"
                onClick={() => onDelete(s.id)}
                title="Delete saved search"
              >
                <Trash2 size={14} />
              </button>
            </li>
          ))}
        </ul>
      )}
      {open && count === 0 && !loading && (
        <div className="border-t border-border/50 px-5 py-4 text-sm text-gray-500">
          Run a query above and it'll be saved here automatically.
        </div>
      )}
    </div>
  )
}
